import React, { useEffect, useState, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Grid,
  Autocomplete
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { nomenclatorV2Api, SegmentDefinitionPayload } from '../../services/nomenclatorV2Api'

interface SegmentDefinition {
  id: string
  code: string
  display_name: string
  display_name_ro?: string
  region_id: string
  subregion_id?: string
  laterality?: string  // Optional - some definitions don't have laterality
  modality: string
  techniques?: string[]  // Also make techniques optional
  purpose_id?: string
  usage_count?: number
  status?: string
}

interface Region {
  id: string
  display_name_ro: string
}

interface Subregion {
  id: string
  parent_region_id: string
  display_name_ro: string
}

interface Laterality {
  id: string
  display_name_ro: string
}

interface Technique {
  id: string
  display_name_ro: string
}

interface Purpose {
  id: string
  display_name_ro: string
}

const MODALITIES = ['CT', 'MR', 'DX', 'US', 'NM', 'PT', 'MG']

export const SegmentCatalogRefactored: React.FC = () => {
  const [definitions, setDefinitions] = useState<SegmentDefinition[]>([])
  const [regions, setRegions] = useState<Region[]>([])
  const [subregions, setSubregions] = useState<Subregion[]>([])
  const [lateralities, setLateralities] = useState<Laterality[]>([])
  const [techniques, setTechniques] = useState<Technique[]>([])
  const [purposes, setPurposes] = useState<Purpose[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRegion, setFilterRegion] = useState<string>('')
  const [filterModality, setFilterModality] = useState<string>('')
  const [filterLaterality, setFilterLaterality] = useState<string>('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [segmentToDelete, setSegmentToDelete] = useState<string | null>(null)

  const [form, setForm] = useState<SegmentDefinitionPayload>({
    code: '',
    display_name: '',
    display_name_ro: '',
    region_id: '',
    subregion_id: '',
    laterality: 'NONE',
    modality: 'CT',
    techniques: [],
    purpose_id: ''
  })

  const loadDefinitions = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await nomenclatorV2Api.listSegmentDefinitions()
      setDefinitions(data)
    } catch (err) {
      console.error(err)
      setError('Nu am putut încărca catalogul de segmente')
    } finally {
      setLoading(false)
    }
  }

  const loadVocabulary = async () => {
    try {
      const [regionsData, subregionsData, lateralitiesData, techniquesData, purposesData] = await Promise.all([
        nomenclatorV2Api.listRegions(),
        nomenclatorV2Api.listSubregions(),
        nomenclatorV2Api.listLateralities(),
        nomenclatorV2Api.listTechniques(),
        nomenclatorV2Api.listPurposes()
      ])
      setRegions(regionsData)
      setSubregions(subregionsData)
      setLateralities(lateralitiesData)
      setTechniques(techniquesData)
      setPurposes(purposesData)
    } catch (err) {
      console.error('Failed to load vocabulary', err)
    }
  }

  useEffect(() => {
    loadDefinitions()
    loadVocabulary()
  }, [])

  const filteredSubregions = useMemo(() => {
    if (!form.region_id) return []
    return subregions.filter((sub) => sub.parent_region_id === form.region_id)
  }, [form.region_id, subregions])

  const filteredDefinitions = useMemo(() => {
    return definitions.filter((def) => {
      const matchesSearch =
        !searchTerm ||
        def.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        def.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (def.display_name_ro && def.display_name_ro.toLowerCase().includes(searchTerm.toLowerCase()))
      const matchesRegion = !filterRegion || def.region_id === filterRegion
      const matchesModality = !filterModality || def.modality === filterModality
      const matchesLaterality = !filterLaterality || def.laterality === filterLaterality
      return matchesSearch && matchesRegion && matchesModality && matchesLaterality
    })
  }, [definitions, searchTerm, filterRegion, filterModality, filterLaterality])

  const stats = useMemo(() => {
    const total = definitions.length
    const active = definitions.filter((d) => d.status !== 'inactive').length
    const totalUsage = definitions.reduce((sum, d) => sum + (d.usage_count || 0), 0)
    return { total, active, totalUsage }
  }, [definitions])

  const handleSubmit = async () => {
    if (!form.code || !form.display_name_ro || !form.region_id || !form.modality || !form.laterality) {
      setError('Cod, Nume RO, Regiune, Modalitate și Lateralitate sunt obligatorii')
      return
    }

    try {
      if (editingId) {
        await nomenclatorV2Api.updateSegmentDefinition(editingId, {
          ...form,
          techniques: form.techniques?.filter((tech) => !!tech) || [],
          laterality: form.laterality || 'NONE',
          subregion_id: form.subregion_id || undefined,
          purpose_id: form.purpose_id || undefined
        })
        setSuccess('Segment actualizat cu succes')
      } else {
        await nomenclatorV2Api.createSegmentDefinition({
          ...form,
          techniques: form.techniques?.filter((tech) => !!tech) || [],
          laterality: form.laterality || 'NONE',
          subregion_id: form.subregion_id || undefined,
          purpose_id: form.purpose_id || undefined
        })
        setSuccess('Segment adăugat cu succes')
      }
      resetForm()
      loadDefinitions()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la salvare')
    }
  }

  const handleEdit = (definition: SegmentDefinition) => {
    setForm({
      code: definition.code,
      display_name: definition.display_name,
      display_name_ro: definition.display_name_ro || '',
      region_id: definition.region_id,
      subregion_id: definition.subregion_id || '',
      laterality: definition.laterality,
      modality: definition.modality,
      techniques: definition.techniques || [],
      purpose_id: definition.purpose_id || ''
    })
    setEditingId(definition.id)
  }

  const handleDeleteClick = (id: string) => {
    setSegmentToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!segmentToDelete) return

    try {
      await nomenclatorV2Api.deleteSegmentDefinition(segmentToDelete)
      setSuccess('Segment șters cu succes')
      loadDefinitions()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la ștergere')
    } finally {
      setDeleteDialogOpen(false)
      setSegmentToDelete(null)
    }
  }

  const resetForm = () => {
    setForm({
      code: '',
      display_name: '',
      display_name_ro: '',
      region_id: '',
      subregion_id: '',
      laterality: 'NONE',
      modality: 'CT',
      techniques: [],
      purpose_id: ''
    })
    setEditingId(null)
  }

  const getRegionName = (regionId: string) => {
    return regions.find((r) => r.id === regionId)?.display_name_ro || regionId
  }

  const getSubregionName = (subregionId: string) => {
    return subregions.find((s) => s.id === subregionId)?.display_name_ro || subregionId
  }

  const getLateralityName = (lateralityId: string) => {
    return lateralities.find((l) => l.id === lateralityId)?.display_name_ro || lateralityId
  }

  if (loading && definitions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  // Compact styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  }
  const tableSx = {
    '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
    '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
    '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.78rem' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)} message={success} />

      {/* Stats - Compact inline */}
      <Box display="flex" gap={3} mb={1.5}>
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Total Segmente</Typography>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{stats.total}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Active</Typography>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{stats.active}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Total Utilizări</Typography>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{stats.totalUsage}</Typography>
        </Box>
      </Box>

      {/* Form - Compact */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AddIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {editingId ? 'Editează Segment' : 'Adaugă Segment Nou'}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" gap={1} flexWrap="wrap">
              <TextField label="Cod" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required size="small" sx={{ width: 140, ...inputSx }} />
              <TextField label="Nume RO" value={form.display_name_ro} onChange={(e) => setForm({ ...form, display_name_ro: e.target.value })} required size="small" sx={{ flex: 1, minWidth: 180, ...inputSx }} />
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap">
              <FormControl size="small" sx={{ width: 100, ...inputSx }}>
                <InputLabel>Modalitate</InputLabel>
                <Select value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })} label="Modalitate">
                  {MODALITIES.map((mod) => (<MenuItem key={mod} value={mod} sx={{ fontSize: '0.8rem' }}>{mod}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140, ...inputSx }}>
                <InputLabel>Regiune</InputLabel>
                <Select value={form.region_id} onChange={(e) => setForm({ ...form, region_id: e.target.value, subregion_id: '' })} label="Regiune">
                  {regions.map((region) => (<MenuItem key={region.id} value={region.id} sx={{ fontSize: '0.8rem' }}>{region.display_name_ro}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140, ...inputSx }}>
                <InputLabel>Subregiune</InputLabel>
                <Select value={form.subregion_id} onChange={(e) => setForm({ ...form, subregion_id: e.target.value })} label="Subregiune" disabled={!form.region_id}>
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>-</MenuItem>
                  {filteredSubregions.map((subregion) => (<MenuItem key={subregion.id} value={subregion.id} sx={{ fontSize: '0.8rem' }}>{subregion.display_name_ro}</MenuItem>))}
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150, ...inputSx }}>
                <InputLabel>Lateralitate</InputLabel>
                <Select value={form.laterality} onChange={(e) => setForm({ ...form, laterality: e.target.value })} label="Lateralitate">
                  {lateralities.map((lat) => (<MenuItem key={lat.id} value={lat.id} sx={{ fontSize: '0.8rem' }}>{lat.display_name_ro}</MenuItem>))}
                </Select>
              </FormControl>
              <Autocomplete
                multiple
                size="small"
                options={techniques}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.display_name_ro)}
                value={form.techniques?.map((techId) => techniques.find((t) => t.id === techId) || techId).filter(Boolean) || []}
                onChange={(_, newValue) => setForm({ ...form, techniques: newValue.map((v) => (typeof v === 'string' ? v : v.id)) })}
                renderInput={(params) => <TextField {...params} label="Tehnici" size="small" />}
                renderTags={(value, getTagProps) => value.map((option, index) => {
                  const tech = typeof option === 'string' ? { id: option, display_name_ro: option } : option
                  return <Chip label={tech.display_name_ro} {...getTagProps({ index })} key={tech.id} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                })}
                sx={{ minWidth: 180, flex: 1, ...inputSx }}
              />
              <FormControl size="small" sx={{ minWidth: 120, ...inputSx }}>
                <InputLabel>Purpose</InputLabel>
                <Select value={form.purpose_id} onChange={(e) => setForm({ ...form, purpose_id: e.target.value })} label="Purpose">
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>-</MenuItem>
                  {purposes.map((purpose) => (<MenuItem key={purpose.id} value={purpose.id} sx={{ fontSize: '0.8rem' }}>{purpose.display_name_ro}</MenuItem>))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleSubmit} disabled={!form.code || !form.display_name_ro || !form.region_id || !form.modality || !form.laterality} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
                {editingId ? 'Salvează' : 'Adaugă'}
              </Button>
              {editingId && <Button variant="outlined" onClick={resetForm} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Anulează</Button>}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters - Compact inline */}
      <Box display="flex" gap={1} mb={1.5} flexWrap="wrap" alignItems="center">
        <TextField label="Caută (cod, nume)" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" sx={{ flex: 1, minWidth: 180, ...inputSx }} />
        <FormControl size="small" sx={{ minWidth: 120, ...inputSx }}>
          <InputLabel>Regiune</InputLabel>
          <Select value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)} label="Regiune">
            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
            {regions.map((r) => (<MenuItem key={r.id} value={r.id} sx={{ fontSize: '0.8rem' }}>{r.display_name_ro}</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100, ...inputSx }}>
          <InputLabel>Modalitate</InputLabel>
          <Select value={filterModality} onChange={(e) => setFilterModality(e.target.value)} label="Modalitate">
            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
            {MODALITIES.map((mod) => (<MenuItem key={mod} value={mod} sx={{ fontSize: '0.8rem' }}>{mod}</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120, ...inputSx }}>
          <InputLabel>Lateralitate</InputLabel>
          <Select value={filterLaterality} onChange={(e) => setFilterLaterality(e.target.value)} label="Lateralitate">
            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
            {lateralities.map((lat) => (<MenuItem key={lat.id} value={lat.id} sx={{ fontSize: '0.8rem' }}>{lat.display_name_ro}</MenuItem>))}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={() => { setSearchTerm(''); setFilterRegion(''); setFilterModality(''); setFilterLaterality(''); }} size="small" sx={{ height: 32, fontSize: '0.75rem', textTransform: 'none' }}>
          Resetează
        </Button>
      </Box>

      {/* Table - Compact */}
      <Card sx={tableSx}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
            Lista Segmente ({filteredDefinitions.length})
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
                <TableCell>Cod</TableCell>
                <TableCell>Nume RO</TableCell>
                <TableCell>Regiune</TableCell>
                <TableCell>Subregiune</TableCell>
                <TableCell>Lateralitate</TableCell>
                <TableCell>Modalitate</TableCell>
                <TableCell>Tehnici</TableCell>
                <TableCell align="right">Utilizări</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDefinitions.map((definition) => (
                <TableRow key={definition.id} hover sx={{ bgcolor: editingId === definition.id ? 'action.hover' : 'inherit' }}>
                  <TableCell><strong>{definition.code}</strong></TableCell>
                  <TableCell>{definition.display_name_ro || definition.display_name}</TableCell>
                  <TableCell>{getRegionName(definition.region_id)}</TableCell>
                  <TableCell>{definition.subregion_id ? getSubregionName(definition.subregion_id) : '-'}</TableCell>
                  <TableCell>{getLateralityName(definition.laterality || 'NONE')}</TableCell>
                  <TableCell>{definition.modality}</TableCell>
                  <TableCell>
                    {definition.techniques && definition.techniques.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                        {definition.techniques.slice(0, 2).map((techId) => {
                          const tech = techniques.find((t) => t.id === techId)
                          return <Chip key={techId} label={tech?.display_name_ro || techId} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                        })}
                        {definition.techniques.length > 2 && <Chip label={`+${definition.techniques.length - 2}`} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">{definition.usage_count || 0}</TableCell>
                  <TableCell><Chip label={definition.status === 'active' ? 'Activ' : 'Inactiv'} size="small" color={definition.status === 'active' ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(definition)} color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(definition.id)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Dialog - Compact */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>Confirmare ștergere</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>Sigur vrei să ștergi segmentul <strong>{segmentToDelete}</strong>?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>Anulează</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>Șterge</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

